import { z } from "zod";

const macAddressSchema = z
  .string()
  .regex(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/, "Invalid MAC address format")
  .trim();

const versionSchema = z.string().max(50, "Version too long").trim().optional();
const modelSchema = z.string().max(100, "Model name too long").trim().optional();
const locationSchema = z.string().max(200, "Location too long").trim().optional();
const notesSchema = z.string().max(1000, "Notes too long").trim().optional();
const serialNumberSchema = z
  .string()
  .max(100, "Serial number too long")
  .trim()
  .optional();
const ipAddressSchema = z.string().ip("Invalid IP address format").optional();

const operatingSystemSchema = z.enum(
  ["windows", "macos", "linux", "android", "ios"],
  { errorMap: () => ({ message: "Invalid operating system" }) },
);

const osArchitectureSchema = z.enum(["x64", "arm64", "x86"], {
  errorMap: () => ({ message: "Invalid OS architecture" }),
});

const complianceStatusSchema = z.enum(["compliant", "non_compliant", "unknown"], {
  errorMap: () => ({ message: "Invalid compliance status" }),
});

const networkInterfaceSchema = z.enum(["ethernet", "wifi", "cellular"], {
  errorMap: () => ({ message: "Invalid network interface" }),
});

const enrollmentCodeSchema = z
  .string()
  .trim()
  .length(6, "Enrollment code must be 6 characters")
  .transform((s) => s.toUpperCase())
  .refine((s) => /^[A-Z0-9]{6}$/.test(s), "Invalid enrollment code");

export const activateDeviceSchema = z.object({
  enrollmentCode: enrollmentCodeSchema,
  macAddress: macAddressSchema,
  serialNumber: serialNumberSchema,
  deviceModel: modelSchema,
  deviceManufacturer: modelSchema,
  operatingSystem: operatingSystemSchema.optional(),
  osVersion: versionSchema,
  osArchitecture: osArchitectureSchema.optional(),
  cpuModel: modelSchema,
  cpuCores: z.number().int().min(1).max(128).optional(),
  ramGb: z.number().int().min(1).max(1024).optional(),
  storageGb: z.number().int().min(1).max(100000).optional(),
  ipAddress: ipAddressSchema,
  networkInterface: networkInterfaceSchema.optional(),
  agentVersion: versionSchema,
  appVersion: versionSchema,
  appLastUpdatedAt: z.number().int().positive().optional(),
  location: locationSchema,
  isEncrypted: z.boolean().optional(),
  securityPatchLevel: versionSchema,
  complianceStatus: complianceStatusSchema.optional(),
  notes: notesSchema,
  name: z.string().max(100).trim().optional(),
});

export const heartbeatDeviceSchema = z.object({
  appVersion: versionSchema,
  agentVersion: versionSchema,
  ipAddress: ipAddressSchema,
});

export type ActivateDeviceRequest = z.infer<typeof activateDeviceSchema>;
export type HeartbeatDeviceRequest = z.infer<typeof heartbeatDeviceSchema>;
